/**
 * @prettier
 * @author Alex Clary
 */
import React from 'react'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import { connect } from 'react-redux'
import LgyInputField from 'app/components/util/lgy-input-field/LgyInputField'
import LgySelectField from 'app/components/util/lgy-select-field/LgySelectField'
import LgyFormSubmit from 'app/components/util/lgy-form-submit/LgyFormSubmit'
import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'
import { required, maxLength, minLength, email } from 'app/utils/validations'
import { formatAllCaps } from 'app/utils/fieldUtils'
import { createServiceAccount } from '../actions/userAdminActions'

// Form validators
const maxLength40 = maxLength(40)
const minLength10 = minLength(10)
const mustEndWithService = value =>
  value && value.length > 0 && !value.endsWith('SERVICE') ? 'Service account name must end with "SERVICE"' : undefined

/**
 * Form for creating a service account (a user)
 *
 * An attempt to make a very simple, easy-to-read, batteries-included form.
 * @author Alex Clary
 */
let CreateServiceAccountForm = ({
  pristine,
  submitting,
  submitFailed,
  submitSucceeded,
  reset,
  handleSubmit,
  error,
  usernameVal,
  accountTypeVal
}) => (
  <form className='create-service-account-form' onSubmit={handleSubmit}>
    {submitSucceeded && accountTypeVal === 'API_ONLY' && (
      <LgyAlertDialog
        type='success'
        heading='Service Account Created'
        text={`API Only Service Account has been created and affiliated with Lender ID ${usernameVal
          .toString()
          .substring(usernameVal.length - 17, usernameVal.length - 7)}`}
      />
    )}
    {submitSucceeded && accountTypeVal === 'SERVICE_ACCOUNT' && (
      <LgyAlertDialog type='success' heading='Service Account Created' text='Service account created successfully' />
    )}
    <header>
      <h2>Create Service Account</h2>
      <p>Creates an account from scratch to be given to external partners to use to call our APIs.</p>
    </header>

    <Field
      name='username'
      type='text'
      component={LgyInputField}
      label='Service Account Username'
      placeholder='e.g. NAMEOFLENDERSERVICE'
      tooltip='Username of the account to create (equivalent to Portal ID)'
      validate={[required, minLength10, maxLength40, mustEndWithService]}
      normalize={formatAllCaps}
      required
    />
    <Field
      name='email'
      type='email'
      component={LgyInputField}
      label='Service Account Email'
      placeholder='e.g. ithelpdesk@lender.com or jane.admin@lender.com'
      tooltip='This should be the email of whoever is responsible for this account. This may be for a specific person or perhaps a generic email that multiple people inside that company monitor.'
      validate={[required, email]}
      required
    />
    <Field
      name='accountType'
      component={LgySelectField}
      label='Account Type'
      tooltip='Select API-only to create an Account for Partners who will use the Loan Review, Pre-Close/GR, Eligibility, or Appraisal APIs. For anything else choose a batch account.'
      validate={[required]}
      required
    >
      <option value='API_ONLY'>API Only</option>
      <option value='SERVICE_ACCOUNT'>Batch/Service</option>
    </Field>
    <LgyFormSubmit
      pristine={pristine}
      submitting={submitting}
      submitFailed={submitFailed}
      submitSucceeded={submitSucceeded}
      error={error}
      reset={reset}
    />
  </form>
)

CreateServiceAccountForm = reduxForm({ form: 'createServiceAccountForm' })(CreateServiceAccountForm)
const selector = formValueSelector('createServiceAccountForm')

const mapDispatchToProps = {
  onSubmit: createServiceAccount
}
const mapStateToProps = state => ({
  usernameVal: selector(state, 'username'),
  accountTypeVal: selector(state, 'accountType')
})

CreateServiceAccountForm = connect(mapStateToProps, mapDispatchToProps)(CreateServiceAccountForm)

export default CreateServiceAccountForm
